import React, { useState } from "react";

const AppContext = React.createContext();

export const AppProvider = ({ children }) => {
    /* #region Main App */
    const [getUserDataToken, setGetUserDataToken] = useState(null); // Handles the token to fetch user data
    const [refreshUserData, setRefreshUserData] = useState(false); // When true, it will refresh the user data
    const [isLoadingApp, setIsLoadingApp] = useState(false); // Handles the loading state of the app
    const [redirectURL, setRedirectURL] = useState(null); // Handles the redirect URL after the user logs in

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Handles the state when there are unsaved changes
    const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false); // Handles the visibility of the unsaved changes popup
    const [userData, setUserData] = useState(null); // Handles the user data that is received from the "getUserData"
    /* #endregion */

    /* #region BOImages */
    const [sidePanelIsLoading, setSidePanelIsLoading] = useState(false); // Handles the loading state of the side panel
    const [sidePanelVisible, setSidePanelVisible] = useState(false); // Handles the visibility of the side panel
    const [sidePanelSection, setSidePanelSection] = useState("Products"); // Handles the section to be displayed in the side panel
    const [sidePanelRetailBrandID, setSidePanelRetailBrandID] = useState(null); // Handles the RetailBrandID to fetch products for the side panel
    const [sidePanelProducts, setSidePanelProducts] = useState([]); // Handles the products to be displayed in the side panel
    const [sidePanelProductSearchName, setSidePanelProductSearchName] = useState(""); // Handles the search name to fetch products for the side panel
    const [sidePanelBOtagProductLink, setSidePanelBOtagProductLink] = useState(null); // Handles the product link to be linked to a BOtag
    const [BOTagsList, setBOTagsList] = useState([]); // Handles the list of BOtags
    const [sidePanelHasNewLinkedProducts, setSidePanelHasNewLinkedProducts] = useState(false); // Handles the visibility of the new linked products dot
    const [sidePanelSelectedProductID, setSidePanelSelectedProductID] = useState(undefined); // Handles the selected product ID
    const [sidePanelSelectedProductNumber, setSidePanelSelectedProductNumber] = useState(undefined); // Handles the selected product number
    const [brandNotSelectedShowError, setBrandNotSelectedShowError] = useState(false); // Handles the visibility of the brand not selected error
    const [sidePanelHasNoProducts, setSidePanelHasNoProducts] = useState(false); // Handles the state when there are no products to display
    const [showAddProductsPopup, setShowAddProductsPopup] = useState(false); // Handles the visibility of the add products popup
    const [boImageProductsImporting, setBOImageProductsImporting] = useState(false); // Handles the state when the products are being imported before returning to the BOImages page
    /* #endregion */

    /* #region AI CHAT SIDE PANEL */
    const [aiChatLoading, setAiChatLoading] = useState(false); // Handles the loading state of the AI chat inside the chat window
    const [aiChatIsVisible, setAiChatIsVisible] = useState(true);
    const [aiChatIsDisabled, setAiChatIsDisabled] = useState(true);
    // ---- AI Chat Window and Side Panel States ----
    const [aiChatWindowOpen, setAiChatWindowOpen] = useState(false); // We no longer use chat window, only side panel
    const [aiChatSidePanelOpen, setAiChatSidePanelOpen] = useState(false);
    // ---- AI Chats conversation states ----
    const [showChatConversation, setShowChatConversation] = useState(true);
    const [showChatAllPanel, setShowChatAllPanel] = useState(false);
    const [showChatArchived, setShowChatArchived] = useState(false);
    // ---- AI Chat Messages ----
    const [aiChatMessages, setAiChatMessages] = useState([]);
    const [aiChatCurrentConversationID, setAiChatCurrentConversationID] = useState(null);
    const [aiActiveChats, setAiActiveChats] = useState([]);
    const [aiArchivedChats, setAiArchivedChats] = useState([]);
    // ---- File Upload State ----
    const [fileUpload, setFileUpload] = useState({ file: null, fileName: null, artifactID: null });
    /* #endregion */

    /* #region GLOBALS */
    const [pageRefreshFromNotification, setPageRefreshFromNotification] = useState(false); // Handles the state when the page is refreshed from a notification

    const [showWelcomePopup, setShowWelcomePopup] = useState(false); // Handles the visibility of the welcome popup
    const [showDownloadAppPopup, setShowDownloadAppPopup] = useState(false); // Handles the visibility of the download app popup
    const [isPwaInstalled, setIsPwaInstalled] = useState(false); // Handles the state when the PWA is installed
    const [firstLoginDone, setFirstLoginDone] = useState(false); // Handles the state when the first login is done
    /* #endregion */

    return (
        <AppContext.Provider
            value={{
                getUserDataToken,
                setGetUserDataToken,
                refreshUserData,
                setRefreshUserData,
                isLoadingApp,
                setIsLoadingApp,
                redirectURL,
                setRedirectURL,

                showUnsavedChangesPopup,
                setShowUnsavedChangesPopup,
                hasUnsavedChanges,
                setHasUnsavedChanges,

                userData,
                setUserData,

                sidePanelIsLoading,
                setSidePanelIsLoading,
                sidePanelVisible,
                setSidePanelVisible,
                sidePanelSection,
                setSidePanelSection,
                sidePanelRetailBrandID,
                setSidePanelRetailBrandID,
                sidePanelProducts,
                setSidePanelProducts,
                sidePanelProductSearchName,
                setSidePanelProductSearchName,
                sidePanelBOtagProductLink,
                setSidePanelBOtagProductLink,
                BOTagsList,
                setBOTagsList,
                sidePanelHasNewLinkedProducts,
                setSidePanelHasNewLinkedProducts,
                sidePanelSelectedProductID,
                setSidePanelSelectedProductID,
                sidePanelSelectedProductNumber,
                setSidePanelSelectedProductNumber,
                brandNotSelectedShowError,
                setBrandNotSelectedShowError,
                sidePanelHasNoProducts,
                setSidePanelHasNoProducts,
                showAddProductsPopup,
                setShowAddProductsPopup,
                boImageProductsImporting,
                setBOImageProductsImporting,

                aiChatLoading,
                setAiChatLoading,
                aiChatIsVisible,
                setAiChatIsVisible,
                aiChatIsDisabled,
                setAiChatIsDisabled,
                aiChatWindowOpen,
                setAiChatWindowOpen,
                aiChatSidePanelOpen,
                setAiChatSidePanelOpen,
                showChatConversation,
                setShowChatConversation,
                showChatAllPanel,
                setShowChatAllPanel,
                showChatArchived,
                setShowChatArchived,
                aiChatMessages,
                setAiChatMessages,
                aiChatCurrentConversationID,
                setAiChatCurrentConversationID,
                aiActiveChats,
                setAiActiveChats,
                aiArchivedChats,
                setAiArchivedChats,
                fileUpload,
                setFileUpload,

                pageRefreshFromNotification,
                setPageRefreshFromNotification,
                showWelcomePopup,
                setShowWelcomePopup,
                showDownloadAppPopup,
                setShowDownloadAppPopup,
                isPwaInstalled,
                setIsPwaInstalled,
                firstLoginDone,
                setFirstLoginDone
            }}>
            {children}
        </AppContext.Provider>
    );
};

// This function is called on logout and on login to reset all states
export const resetImportantStatesOnLogin = AppContext => {
    const {
        //setSidePanelSection, //TODO: VERIFY ALL THESE CONTEXTS
        //setSidePanelLabelBrandID,
        //setSidePanelProducts,
        //setSidePanelProductSearchName,
        //setBOTagsList,

        setAiChatLoading,
        setAiChatIsVisible,
        setAiChatIsDisabled,
        setAiChatWindowOpen,
        setAiChatSidePanelOpen,
        setShowChatConversation,
        setShowChatAllPanel,
        setShowChatArchived,
        setAiChatMessages,
        setAiChatCurrentConversationID,
        setAiActiveChats,
        setAiArchivedChats,
        setFileUpload
    } = AppContext;

    //setSidePanelSection("Products");
    //setSidePanelLabelBrandID(null);
    //setSidePanelProducts([]);
    //setSidePanelProductSearchName("");
    //setBOTagsList([]);

    setAiChatLoading(false);
    setAiChatIsVisible(true);
    setAiChatIsDisabled(true);
    setAiChatWindowOpen(false);
    setAiChatSidePanelOpen(false);
    setShowChatConversation(true);
    setShowChatAllPanel(false);
    setShowChatArchived(false);
    setAiChatMessages([]);
    setAiChatCurrentConversationID(null);
    setAiActiveChats([]);
    setAiArchivedChats([]);
    setFileUpload({ file: null, fileName: null, artifactID: null });
};
export default AppContext;
